import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { SummaryCard } from '@nucleus-commerce/admin-kit/forms';
import type { ProductSummaryStatus } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import type { OtherStore } from '../hooks/use-other-stores';
import { PRODUCT_ENGAGEMENT_DAYS, useProductEngagement } from '../hooks/use-product-engagement';
import { storeProductLiveState } from '../schemas/live-state';

import { StoreLiveChip } from './store-live-chip';

export interface StoreOtherStoresCardProps {
  others: readonly OtherStore[];
  masterStatus: ProductSummaryStatus;
}

/** Where else this product is sold, collapsed: each store's page and what shoppers get there. */
export function StoreOtherStoresCard({ others, masterStatus }: StoreOtherStoresCardProps) {
  const { t } = useTranslation('storeCatalog');
  return (
    <SummaryCard
      title={t('stores.title')}
      meta={t('storePage.stores.meta', { count: others.length })}
      collapsible
    >
      {others.length === 0 ? (
        <p className="text-muted-foreground text-[13px]">{t('storePage.stores.none')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {others.map(({ store, row }) => (
            <li key={store.id} className="flex items-center justify-between gap-2">
              <Link
                to="/s/$storeId/catalog/$storeProductId"
                params={{ storeId: store.id, storeProductId: row.id }}
                className="text-primary min-w-0 truncate text-[13px] hover:underline"
              >
                {store.name}
              </Link>
              <StoreLiveChip
                state={storeProductLiveState({
                  status: row.status,
                  masterStatus,
                  publishAt: row.publishAt,
                })}
              />
            </li>
          ))}
        </ul>
      )}
    </SummaryCard>
  );
}

export interface StoreInsightsCardProps {
  storeId: string;
  productId: string;
}

/** Recent sales of this product in this store, collapsed. Needs `reports.read`; gate it. */
export function StoreInsightsCard({ storeId, productId }: StoreInsightsCardProps) {
  const { t } = useTranslation('storeCatalog');
  const format = useFormat();
  const query = useProductEngagement(productId);
  const here = query.data?.stores.find((store) => store.storeId === storeId);
  const metrics: Array<[string, string]> = here
    ? [
        [t('storePage.insights.sold'), format.number(here.units)],
        [t('storePage.insights.revenue'), format.money(here.revenue, { notation: 'compact' })],
        [t('storePage.insights.views'), format.number(here.views)],
      ]
    : [];

  let body;
  if (query.isPending) body = <Skeleton className="h-10 w-full" />;
  else if (query.isError) {
    body = <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />;
  } else if (!here) {
    body = (
      <p className="text-muted-foreground text-[13px]">
        {t('storePage.insights.none', { count: PRODUCT_ENGAGEMENT_DAYS })}
      </p>
    );
  } else {
    body = (
      <>
        <span className="text-muted-foreground text-xs">
          {t('storePage.insights.window', { count: query.data.days })}
        </span>
        <dl className="grid grid-cols-3 gap-2">
          {metrics.map(([label, value]) => (
            <div key={label} className="flex flex-col">
              <dd className="text-sm font-semibold tabular-nums">{value}</dd>
              <dt className="text-muted-foreground text-xs">{label}</dt>
            </div>
          ))}
        </dl>
      </>
    );
  }

  return (
    <SummaryCard
      title={t('storePage.insights.title')}
      meta={here ? t('storePage.insights.meta', { count: here.units }) : null}
      collapsible
    >
      {body}
    </SummaryCard>
  );
}
